import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ProgramRequest } from '@/types'
import type { RegistrationErrors, RegistrationField, RegistrationInput } from '@/lib/registration'

import { useEffect, useState } from 'react'
import { buttonClass } from '@/lib/button'
import { formatMobile } from '@/lib/validation'
import { REGISTRATION_FIELDS, emptyRegistration, validateRegistration } from '@/lib/registration'
import SectionHeading from '@/components/landing/section-heading'

import {
    ArrowRight,
    CircleAlert,
    CircleCheck,
    GraduationCap,
    LoaderCircle
} from 'lucide-react'

import {
    Field,
    MobileInput,
    SelectInput,
    TextArea,
    TextInput,
    fieldDescription
} from '@/components/landing/form-fields'

import {
    centerOptions,
    contactPhones,
    passingYearOptions,
    programOptions,
    qualificationOptions,
    registrationSteps
} from '@/constants/content'

interface RegisterSectionProps {
    programRequest: ProgramRequest | null
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

type Confirmation = {
    firstName: string
    mobile: string
}

const ELIGIBILITY = [
    "B.Tech students and graduates",
    "Degree students and graduates",
    "Diploma students and graduates"
]

const MOBILE_HINT = "We'll call you on this number."

const RegisterSection: FC<RegisterSectionProps> = ({ programRequest }): ReactNode => {
    const [values, setValues] = useState<RegistrationInput>(emptyRegistration)
    const [website, setWebsite] = useState('')
    const [errors, setErrors] = useState<RegistrationErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [formError, setFormError] = useState('')
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)

    const primaryPhone = contactPhones[0]

    useEffect(() => {
        if (!programRequest) return

        setValues((current) => ({ ...current, program: programRequest.id }))
        setErrors((current) => ({ ...current, program: undefined }))
        setStatus((current) => (current === 'success' ? 'idle' : current))
    }, [programRequest])

    useEffect(() => {
        if (status === 'success') document.getElementById('register-success-title')?.focus()
    }, [status])

    const update = (field: RegistrationField, value: string): void => {
        setValues((current) => ({ ...current, [field]: value }))
        setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current))

        if (status === 'error') {
            setStatus('idle')
            setFormError('')
        }
    }

    const handleChange = (field: RegistrationField) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        update(field, event.target.value)
    }

    const focusFirstError = (nextErrors: RegistrationErrors): void => {
        const first = REGISTRATION_FIELDS.find((field) => nextErrors[field])

        if (first) document.getElementById(`register-${first}`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const nextErrors = validateRegistration(values)

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length > 0) {
            focusFirstError(nextErrors)

            return
        }

        setStatus('submitting')
        setFormError('')

        try {
            const response = await fetch('/api/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, website })
            })

            const data = (await response.json().catch(() => null)) as { message?: string, errors?: RegistrationErrors } | null

            if (response.ok) {
                setConfirmation({
                    firstName: values.fullName.trim().split(/\s+/)[0] ?? '',
                    mobile: formatMobile(values.mobile)
                })
                setValues(emptyRegistration)
                setWebsite('')
                setErrors({})
                setStatus('success')

                return
            }

            if (response.status === 400 && data?.errors) {
                setErrors(data.errors)
                setStatus('idle')
                focusFirstError(data.errors)

                return
            }

            setFormError(data?.message ?? `Something went wrong on our side. Please try again, or call us on ${primaryPhone.display}.`)
            setStatus('error')
        }

        catch {
            setFormError(`We couldn't reach our server. Check your connection and try again, or call us on ${primaryPhone.display}.`)
            setStatus('error')
        }
    }

    const startOver = (): void => {
        setConfirmation(null)
        setStatus('idle')
    }

    return (
        <section id='register' aria-labelledby='register-title' className='relative isolate scroll-mt-20 overflow-hidden py-20 sm:py-24 lg:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 bg-linear-to-b from-background via-secondary/70 to-background' />
            <div aria-hidden='true' className='pointer-events-none absolute -right-40 top-20 -z-10 size-[30rem] rounded-full bg-primary/10 blur-[120px]' />

            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8'>
                <div>
                    <SectionHeading
                        id='register-title'
                        eyebrow="Register"
                        title="Start your Webify journey."
                        description="Share a few details and our team will call you to confirm your track, batch timings and fees."
                    />

                    <div className='mt-8'>
                        <h3 className='text-sm font-semibold text-foreground'>
                            Who can register
                        </h3>

                        <ul className='mt-3 flex flex-wrap gap-2'>
                            {ELIGIBILITY.map((item) => (
                                <li key={item} className='inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm text-foreground'>
                                    <GraduationCap className='size-4 text-primary' aria-hidden='true' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='mt-10'>
                        <h3 className='text-sm font-semibold text-foreground'>
                            What happens next
                        </h3>

                        <ol className='mt-5 space-y-6'>
                            {registrationSteps.map((step, index) => (
                                <li key={step.title} className='relative flex gap-4'>
                                    {index < registrationSteps.length - 1 && (
                                        <span aria-hidden='true' className='absolute left-5 top-11 h-[calc(100%-1.5rem)] w-px bg-border' />
                                    )}

                                    <span className='relative flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border'>
                                        <step.icon className='size-4' aria-hidden='true' />
                                    </span>

                                    <div className='pt-1.5'>
                                        <p className='text-[15px] font-semibold text-foreground'>
                                            {step.title}
                                        </p>

                                        <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                            {step.text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <p className='mt-10 text-sm text-muted-foreground'>
                        Prefer to talk first? Call{' '}
                        <a href={primaryPhone.href} className='font-semibold text-primary underline-offset-4 hover:underline'>
                            {primaryPhone.display}
                        </a>
                    </p>
                </div>

                <div className='relative rounded-2xl border border-border bg-card p-6 shadow-xl shadow-ink/5 sm:p-8'>
                    {status === 'success' && confirmation ? (
                        <div className='flex flex-col items-center py-10 text-center motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95'>
                            <span className='flex size-16 items-center justify-center rounded-full bg-teal/15 text-teal-strong'>
                                <CircleCheck className='size-8' aria-hidden='true' />
                            </span>

                            <h3 id='register-success-title' tabIndex={-1} className='mt-6 text-2xl font-semibold text-foreground focus:outline-none'>
                                Registration received
                            </h3>

                            <p className='mt-3 max-w-sm text-[15px] leading-relaxed text-muted-foreground'>
                                Thanks{confirmation.firstName ? `, ${confirmation.firstName}` : ''}. Our team will call you on +91 {confirmation.mobile} to confirm your track, batch timings and fees.
                            </p>

                            <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                                <a href='#programs' className={buttonClass('outline', 'md')}>
                                    Browse programs
                                </a>

                                <button type='button' onClick={startOver} className={buttonClass('primary', 'md')}>
                                    Register someone else
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form noValidate onSubmit={handleSubmit} aria-labelledby='register-form-title'>
                            <h3 id='register-form-title' className='text-xl font-semibold text-foreground'>
                                Registration form
                            </h3>

                            <p className='mt-1 text-sm text-muted-foreground'>
                                Fields marked <span className='text-primary'>*</span> are required.
                            </p>

                            <div className='mt-6 grid gap-4 sm:grid-cols-2'>
                                <Field id='register-fullName' label="Full name" required error={errors.fullName} className='sm:col-span-2'>
                                    <TextInput
                                        id='register-fullName'
                                        name='fullName'
                                        autoComplete='name'
                                        value={values.fullName}
                                        onChange={handleChange('fullName')}
                                        placeholder="Your full name"
                                        invalid={Boolean(errors.fullName)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-fullName', errors.fullName)}
                                    />
                                </Field>

                                <Field id='register-mobile' label="Mobile number" required hint={MOBILE_HINT} error={errors.mobile}>
                                    <MobileInput
                                        id='register-mobile'
                                        name='mobile'
                                        value={values.mobile}
                                        onValueChange={(value) => update('mobile', value)}
                                        placeholder="98765 43210"
                                        invalid={Boolean(errors.mobile)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-mobile', errors.mobile, MOBILE_HINT)}
                                    />
                                </Field>

                                <Field id='register-email' label="Email" optional error={errors.email}>
                                    <TextInput
                                        id='register-email'
                                        name='email'
                                        type='email'
                                        inputMode='email'
                                        autoComplete='email'
                                        value={values.email}
                                        onChange={handleChange('email')}
                                        placeholder="you@example.com"
                                        invalid={Boolean(errors.email)}
                                        aria-describedby={fieldDescription('register-email', errors.email)}
                                    />
                                </Field>

                                <Field id='register-qualification' label="Qualification" required error={errors.qualification}>
                                    <SelectInput
                                        id='register-qualification'
                                        name='qualification'
                                        value={values.qualification}
                                        onChange={handleChange('qualification')}
                                        options={qualificationOptions}
                                        placeholder="Select qualification"
                                        invalid={Boolean(errors.qualification)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-qualification', errors.qualification)}
                                    />
                                </Field>

                                <Field id='register-passingYear' label="Year of passing" required error={errors.passingYear}>
                                    <SelectInput
                                        id='register-passingYear'
                                        name='passingYear'
                                        value={values.passingYear}
                                        onChange={handleChange('passingYear')}
                                        options={passingYearOptions}
                                        placeholder="Select year"
                                        invalid={Boolean(errors.passingYear)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-passingYear', errors.passingYear)}
                                    />
                                </Field>

                                <Field id='register-college' label="College or institution" optional error={errors.college} className='sm:col-span-2'>
                                    <TextInput
                                        id='register-college'
                                        name='college'
                                        autoComplete='organization'
                                        value={values.college}
                                        onChange={handleChange('college')}
                                        placeholder="Where you study or studied"
                                        invalid={Boolean(errors.college)}
                                        aria-describedby={fieldDescription('register-college', errors.college)}
                                    />
                                </Field>

                                <Field id='register-center' label="Training center" required error={errors.center}>
                                    <SelectInput
                                        id='register-center'
                                        name='center'
                                        value={values.center}
                                        onChange={handleChange('center')}
                                        options={centerOptions}
                                        placeholder="Select a center"
                                        invalid={Boolean(errors.center)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-center', errors.center)}
                                    />
                                </Field>

                                <Field id='register-program' label="Program" required error={errors.program}>
                                    <SelectInput
                                        id='register-program'
                                        name='program'
                                        value={values.program}
                                        onChange={handleChange('program')}
                                        options={programOptions}
                                        placeholder="Select a program"
                                        invalid={Boolean(errors.program)}
                                        aria-required='true'
                                        aria-describedby={fieldDescription('register-program', errors.program)}
                                    />
                                </Field>

                                <Field id='register-message' label="Anything we should know?" optional error={errors.message} className='sm:col-span-2'>
                                    <TextArea
                                        id='register-message'
                                        name='message'
                                        rows={3}
                                        maxLength={1000}
                                        value={values.message}
                                        onChange={handleChange('message')}
                                        placeholder="Preferred batch timings, questions about fees, or anything else"
                                        invalid={Boolean(errors.message)}
                                        aria-describedby={fieldDescription('register-message', errors.message)}
                                    />
                                </Field>
                            </div>

                            <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                                <label htmlFor='register-website'>Website</label>

                                <input
                                    id='register-website'
                                    name='website'
                                    type='text'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={website}
                                    onChange={(event) => setWebsite(event.target.value)}
                                />
                            </div>

                            <div aria-live='polite'>
                                {status === 'error' && formError && (
                                    <div className='mt-6 flex gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-3.5 py-3 text-sm leading-relaxed text-destructive'>
                                        <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />

                                        <p>
                                            {formError}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button type='submit' disabled={status === 'submitting'} className={buttonClass('primary', 'lg', 'group mt-6 w-full')}>
                                {status === 'submitting' ? (
                                    <>
                                        <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                        Sending your registration
                                    </>
                                ) : (
                                    <>
                                        Submit registration
                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                    </>
                                )}
                            </button>

                            <p className='mt-4 text-center text-xs leading-relaxed text-muted-foreground'>
                                We only use these details to contact you about your registration.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
}

export default RegisterSection
